import { useState, useMemo } from 'react'
import { Download, Filter, Search } from 'lucide-react'
import DateInput from '../ui/DateInput'

function formatR(n) {
  return `R${(Number(n) || 0).toFixed(2)}`
}

const CASH_OUT_TYPES = ['All', 'Expense', 'Float Change', 'Payout', 'Other']

export default function CashOutLog({ records }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('All')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Flatten all cash-out items with their dates
  const allItems = useMemo(() => {
    const items = []
    for (const rec of records) {
      for (const item of (rec.cashOutItems || [])) {
        items.push({
          date: rec.date,
          description: item.description || '',
          amount: Number(item.amount) || 0,
          type: item.type || 'Other',
        })
      }
    }
    return items.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
  }, [records])

  // Filtered items
  const filtered = useMemo(() => {
    let result = allItems
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(item =>
        item.description.toLowerCase().includes(term) || item.type.toLowerCase().includes(term)
      )
    }
    if (typeFilter !== 'All') {
      result = result.filter(item => item.type === typeFilter)
    }
    if (startDate) {
      result = result.filter(item => item.date >= startDate)
    }
    if (endDate) {
      result = result.filter(item => item.date <= endDate)
    }
    return result
  }, [allItems, searchTerm, typeFilter, startDate, endDate])

  const totalFiltered = filtered.reduce((s, item) => s + item.amount, 0)

  const handleExport = async () => {
    try {
      const XLSX = await import('xlsx')
      const data = filtered.map(item => ({
        Date: item.date,
        Description: item.description,
        Amount: item.amount,
        Type: item.type,
      }))
      const ws = XLSX.utils.json_to_sheet(data)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Cash Out Log')
      XLSX.writeFile(wb, `cash-out-log-${new Date().toISOString().split('T')[0]}.xlsx`)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search descriptions..."
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 pl-8 text-sm text-white outline-none focus:border-brand-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-500" />
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white"
          >
            {CASH_OUT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <DateInput value={startDate} onChange={setStartDate} />
          <span className="text-gray-500 text-sm">to</span>
          <DateInput value={endDate} onChange={setEndDate} />
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 bg-green-800 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm"
        >
          <Download size={14} /> Export Excel
        </button>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-400">{filtered.length} items</span>
        <span className="text-white font-medium">Total: {formatR(totalFiltered)}</span>
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[100px_1fr_100px_120px] gap-2 text-xs text-gray-500 font-medium px-4 py-2 border-b border-gray-800">
          <span>Date</span>
          <span>Description</span>
          <span>Amount</span>
          <span>Type</span>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-600 text-sm">No cash-out items found</div>
          )}
          {filtered.map((item, i) => (
            <div key={i} className="grid grid-cols-[100px_1fr_100px_120px] gap-2 items-center px-4 py-2 text-sm border-b border-gray-800/50 hover:bg-gray-800/30">
              <span className="text-gray-400 font-mono text-xs">{item.date}</span>
              <span className="text-white text-xs">{item.description || '(no description)'}</span>
              <span className="text-white text-xs">{formatR(item.amount)}</span>
              <span className={`text-xs px-2 py-0.5 rounded inline-block w-fit ${
                item.type === 'Expense' ? 'bg-red-950 text-red-400' :
                item.type === 'Float Change' ? 'bg-amber-950 text-amber-400' :
                item.type === 'Payout' ? 'bg-blue-950 text-blue-400' :
                'bg-gray-800 text-gray-400'
              }`}>
                {item.type}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
